import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import {
  Music2, Mail, Sparkles, Star, Loader2, ArrowLeft, Play, Heart,
  MessageSquare, Share2, Eye,
} from "lucide-react";

const CHANNEL_TITLE = {
  tiktok: { label: "TikTok", icon: Music2, accent: "#FF0050" },
  sendgrid: { label: "Email · SendGrid", icon: Mail, accent: "#002FA7" },
  "openai-ads": { label: "OpenAI Ads", icon: Sparkles, accent: "#10A37F" },
  reviews: { label: "Reviews", icon: Star, accent: "#F59E0B" },
};

const fmt = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1) + "M";
  if (v >= 1_000) return (v / 1_000).toFixed(1) + "K";
  return v.toLocaleString();
};

export default function ClientChannelPage() {
  const { key } = useParams();
  const [params] = useSearchParams();
  const adminClientId = params.get("client_id");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const meta = CHANNEL_TITLE[key] || { label: key, icon: Music2, accent: "#002FA7" };
  const Icon = meta.icon;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const qs = adminClientId ? `?client_id=${encodeURIComponent(adminClientId)}` : "";
        const { data } = await api.get(`/client-portal/channels/${key}${qs}`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || "Failed to load channel data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [key, adminClientId]);

  return (
    <div className="max-w-6xl mx-auto p-4 lg:p-8 space-y-5" data-testid={`client-channel-${key}`}>
      <Link to={`/portal/dashboard${adminClientId ? `?client_id=${encodeURIComponent(adminClientId)}` : ""}`}
        className="inline-flex items-center gap-1.5 text-[12px] text-zinc-500 hover:text-zinc-900"
        data-testid="channel-back">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to dashboard
      </Link>

      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-sm flex items-center justify-center text-white"
          style={{ background: meta.accent }}>
          <Icon className="h-5 w-5" />
        </div>
        <h1 className="font-display font-bold text-2xl tracking-tight">{meta.label}</h1>
      </div>

      {loading && (
        <div className="p-12 text-center text-sm text-zinc-500">
          <Loader2 className="h-5 w-5 inline mr-2 animate-spin" /> Loading…
        </div>
      )}

      {!loading && data && key === "tiktok" && <TikTokDetail data={data} accent={meta.accent} />}
      {!loading && data && key === "sendgrid" && <SendGridDetail data={data} accent={meta.accent} />}
      {!loading && data && key === "openai-ads" && <OpenAIAdsDetail data={data} accent={meta.accent} />}
      {!loading && data && key === "reviews" && <ReviewsDetail data={data} accent={meta.accent} />}
    </div>
  );
}

function TikTokDetail({ data, accent }) {
  const p = data.profile || {};
  const chart = (data.daily_history || []).map((r) => ({
    date: (r.date || "").slice(5),
    views: Number((r.metrics || {}).video_views || 0),
    followers: Number((r.metrics || {}).followers || 0),
  }));
  return (
    <div className="space-y-5">
      {p.display_name && (
        <Card className="rounded-sm border-zinc-200">
          <CardContent className="p-4 flex items-center gap-4">
            {p.avatar_url && <img src={p.avatar_url} className="h-14 w-14 rounded-sm border" alt="" />}
            <div>
              <div className="font-display font-bold text-lg">{p.display_name}</div>
              {p.username && <div className="text-[11px] font-mono text-zinc-500">@{p.username}</div>}
            </div>
            <div className="ml-auto grid grid-cols-3 gap-3 text-right">
              <Mini label="Followers" value={fmt(p.follower_count)} />
              <Mini label="Likes" value={fmt(p.likes_count)} />
              <Mini label="Videos" value={fmt(p.video_count)} />
            </div>
          </CardContent>
        </Card>
      )}
      {chart.length > 1 && (
        <Card className="rounded-sm border-zinc-200" data-testid="tiktok-detail-chart">
          <CardContent className="p-4">
            <div className="label-overline mb-2">Daily trend (CSV history)</div>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="views" stroke={accent} strokeWidth={2} dot={false} name="Video views" />
                <Line type="monotone" dataKey="followers" stroke="#00F2EA" strokeWidth={2} dot={false} name="Followers" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
      {data.top_videos?.length > 0 && (
        <Card className="rounded-sm border-zinc-200">
          <CardContent className="p-0 overflow-hidden">
            <div className="px-4 py-2 bg-zinc-50/60 border-b border-zinc-100 label-overline">Top videos · by views</div>
            <table className="w-full text-sm">
              <tbody>
                {data.top_videos.map((v) => (
                  <tr key={v.video_id} className="border-b border-zinc-50">
                    <td className="px-3 py-2 w-12">{v.cover_image_url && <img src={v.cover_image_url} alt="" className="h-12 w-8 rounded-sm object-cover" />}</td>
                    <td className="px-3 py-2 text-[12px] max-w-[420px] truncate">{v.title || "Untitled"}</td>
                    <td className="px-3 py-2 text-right font-mono text-[11px]"><Eye className="h-3 w-3 inline mr-1" />{fmt(v.view_count)}</td>
                    <td className="px-3 py-2 text-right font-mono text-[11px]"><Heart className="h-3 w-3 inline mr-1" />{fmt(v.like_count)}</td>
                    <td className="px-3 py-2 text-right font-mono text-[11px]"><MessageSquare className="h-3 w-3 inline mr-1" />{fmt(v.comment_count)}</td>
                    <td className="px-3 py-2 text-right font-mono text-[11px]"><Share2 className="h-3 w-3 inline mr-1" />{fmt(v.share_count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function SendGridDetail({ data, accent }) {
  const chart = (data.daily_stats || []).map((r) => ({
    date: (r.date || "").slice(5),
    delivered: Number((r.metrics || {}).delivered || 0),
    opens: Number((r.metrics || {}).unique_opens || 0),
    clicks: Number((r.metrics || {}).unique_clicks || 0),
  }));
  return (
    <Card className="rounded-sm border-zinc-200" data-testid="sendgrid-detail-card">
      <CardContent className="p-4">
        <div className="label-overline mb-2">Last 30 days</div>
        {chart.length === 0 ? (
          <div className="text-sm text-zinc-500 py-6 text-center">No SendGrid stats yet.</div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="delivered" stroke={accent} strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="opens" stroke="#10B981" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="clicks" stroke="#F59E0B" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

function OpenAIAdsDetail({ data, accent }) {
  const chart = (data.insights || []).map((r) => ({
    date: (r.date || "").slice(5),
    impressions: Number((r.metrics || {}).impressions || 0),
    clicks: Number((r.metrics || {}).clicks || 0),
    spend: Number((r.metrics || {}).spend || 0),
  }));
  return (
    <Card className="rounded-sm border-zinc-200" data-testid="openai-ads-detail-card">
      <CardContent className="p-4">
        <div className="label-overline mb-2">Last 60 days</div>
        {chart.length === 0 ? (
          <div className="text-sm text-zinc-500 py-6 text-center">No OpenAI Ads insights yet.</div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="impressions" stroke={accent} strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="clicks" stroke="#002FA7" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="spend" stroke="#F59E0B" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

function ReviewsDetail({ data }) {
  const list = [
    ...(data.google || []).map((r) => ({ source: "Google", ...r,
      ts: r.create_time, body: r.comment, by: r.reviewer })),
    ...(data.trustpilot || []).map((r) => ({ source: "Trustpilot", ...r,
      ts: r.created_at, body: r.text, by: r.author })),
    ...(data.productreview || []).map((r) => ({ source: "ProductReview", ...r,
      ts: r.created_at, body: r.review_text || r.title, by: r.author })),
  ].sort((a, b) => String(b.ts || "").localeCompare(String(a.ts || "")));

  return (
    <Card className="rounded-sm border-zinc-200" data-testid="reviews-detail-card">
      <CardContent className="p-0">
        {list.length === 0 ? (
          <div className="p-6 text-sm text-zinc-500 text-center">No reviews yet.</div>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {list.slice(0, 50).map((r, i) => (
              <li key={`rv-${i}`} className="p-3 flex gap-3" data-testid={`review-${r.source}-${i}`}>
                <Badge variant="outline" className="rounded-sm text-[10px] uppercase font-mono h-5">{r.source}</Badge>
                <div className="flex-1 min-w-0">
                  <div className="text-[12px]">
                    <Star className="h-3 w-3 inline text-amber-500 mr-1" />
                    <strong>{r.rating || "—"}</strong>
                    <span className="text-zinc-500"> · {r.by || "Anonymous"}</span>
                    {r.ts && <span className="text-[10px] font-mono text-zinc-400 ml-2">{new Date(r.ts).toLocaleDateString()}</span>}
                  </div>
                  {r.body && <div className="text-[12px] text-zinc-700 mt-0.5">{String(r.body).slice(0, 280)}</div>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function Mini({ label, value }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="font-mono font-semibold text-sm">{value}</div>
    </div>
  );
}
